'use client'

import useSWRInfinite, { SWRInfiniteConfiguration, unstable_serialize } from 'swr/infinite'
import { RequestDocument, Variables } from 'graphql-request'
import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { useMemo } from 'react'
import { useMicrositeSettingsContext } from '@dustin-web/microsite-components/src/hooks/use-microsite-settings'
import { keepPreviousResultMiddleware } from '../middleware'
import { useCookieState } from '@dustin-web/microsite-components/src/hooks/use-cookie-state'
import {
  dangerously_getGraphqlFetcher,
  DEFAULT_GRAPHQL_CONTEXT,
  getCacheKeyFromDocument,
  GraphqlContext,
} from './graphql-fetcher'

const DEFAULT_SWR_OPTIONS: SWRInfiniteConfiguration = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
}

const getCacheKeyFactory =
  (document: RequestDocument, variables: Variables = {}) =>
  <TQuery>(index: number, _previousPageData: TQuery) => {
    const page = index + 1
    const cacheKey = getCacheKeyFromDocument(document, { ...variables, page })

    return [cacheKey, page]
  }

export const getInfiniteCacheKeyFromDocument = (document: RequestDocument, variables: Variables) =>
  unstable_serialize(getCacheKeyFactory(document, variables))

export const useGraphqlInfinite = <TQuery = unknown, TVariables = Record<string, unknown>>(
  document: RequestDocument | TypedDocumentNode<TQuery, TVariables>,
  {
    variables,
    context,
    swrOptions,
  }: {
    variables?: TVariables & { page: number }
    context?: GraphqlContext
    swrOptions?: SWRInfiniteConfiguration
  }
) => {
  const swrOptionsWithDefaults = { ...DEFAULT_SWR_OPTIONS, ...swrOptions }
  const contextWithDefaults = { ...DEFAULT_GRAPHQL_CONTEXT, ...context }
  const micrositeSettings = useMicrositeSettingsContext()
  const { cookieHeader } = useCookieState()

  const fetcher = useMemo(
    () => dangerously_getGraphqlFetcher(micrositeSettings, cookieHeader, contextWithDefaults),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const middleware = contextWithDefaults.keepPreviousResult ? [keepPreviousResultMiddleware] : []

  return useSWRInfinite<TQuery>(
    getCacheKeyFactory(document, variables),
    ([_cacheKey, page]) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return fetcher.fetchGraphql<TQuery, TVariables>(document, { ...variables, page })
    },
    {
      use: middleware,
      ...swrOptionsWithDefaults,
    }
  )
}
